import React, { useEffect, useRef, useState } from "react";
import List from "../../../components/List/List";
import BackupService from "../../../services/BackupService";
import "./ListBackup.css";
import { useUserStore } from "../../../contexts/userStore";
import Pagination from "../../../components/Pagination/Pagination";
import Card from "../../../components/Card/Card";
import { AxiosError } from "axios";
import { DataSource } from "../../../components/DataSource/DataSource";
import { formatBytes } from "../../../utils/bytes";
import { useSSEContext } from "../../../contexts/sse";
import { BackupStatus } from "../../../utils/dictionary";
import { formatDate } from "../../../utils/date";
import { DatetimeWithRelative } from "../../../components/DatetimeWithRelative/DatetimeWithRelative";

const ListBackup = (props) => {
	const [backups, setBackups] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [isLoading, setIsLoading] = useState(true);
	const { selectedLicenceId, selectedCondominio } = useUserStore();
	const [currentPage, setCurrentPage] = useState(1);
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "createdAt",
		orderDirection: "DESC",
	});
	const { sseReceived } = useSSEContext();
	const cancelTokenRef = useRef(null);

	const getBackups = async (page, searchText, licenceId, collumnClicked) => {
		if (!licenceId) return;
		if (cancelTokenRef.current) {
			cancelTokenRef.current.abort();
		}
		cancelTokenRef.current = new AbortController();
		setIsLoading(true);
		const response = await BackupService.listBackupsPaginated(page, searchText, licenceId, collumnClicked, cancelTokenRef.current.signal);
		setIsLoading(false);
		if (response instanceof AxiosError) {
			props.setError?.(response);
			return;
		}
		setCurrentPage(page);
		if (Array.isArray(response?.data.items)) {
			setBackups(response.data.items);
			setMaxPage(response.data.pagination.totalPages);
		} else {
			setBackups([]);
		}
	};

	React.useEffect(() => {
		getBackups(currentPage, props.filters, selectedLicenceId, collumnClicked);
	}, [props.filters, currentPage, selectedLicenceId, collumnClicked, props.refreshKey]);

	useEffect(() => {
		async function onSseReceived() {
			if (!sseReceived.content) return;
			if (selectedCondominio !== Number(sseReceived.content.unitId))
				return;
			if (sseReceived?.action === 'backupStatus') {
				setBackups((prevBackups) => {
					return prevBackups.map((backup) => {
						if (Number(backup.id) === Number(sseReceived.content.backupId)) {
							return {
								...backup,
								status: sseReceived.content.status,
								uploadedAt: sseReceived.content.uploadedAt || backup.uploadedAt,
							};
						}
						return backup;
					});
				});
			}
			if (['backupInitiated', 'backupImported'].includes(sseReceived?.action)) {
				getBackups(currentPage, props.filters, selectedLicenceId, collumnClicked);
			}
		}
		onSseReceived();
	}, [sseReceived]);

	return (
		<React.Fragment>
			<div className="list-container list-visibility">
				<List
					isLoading={isLoading}
					handleCollumnTitleClick={(collumnClicked) =>
						setCollumnClicked(collumnClicked)
					}
					listEntityName={'backups'}
					collumsObjIndex={[
						{
							collumnLabel: "Nome", index: "bucketKey", type: "text", custom: ({ row }) => {
								const pathItems = row.bucketKey.split('/');
								return pathItems[pathItems.length - 1];
							}
						},
						{
							collumnLabel: "Iniciado em", index: "createdAt", type: "date", custom: ({ row }) => {
								return row.createdAt ? <DatetimeWithRelative datetime={row.createdAt} /> : '---';
							}
						},
						{
							collumnLabel: "Enviado em", index: "uploadedAt", type: "date", custom: ({ row }) => {
								return row.uploadedAt ? <DatetimeWithRelative datetime={row.uploadedAt} /> : ([5].includes(row.status) ? '---' : 'Em andamento');
							}
						},
						{
							collumnLabel: "Status", index: "status", forceLabel: true, type: "text", custom: ({ row }) => {
								return BackupStatus[row.status] || row.status;
							}
						},
						{
							collumnLabel: "Enviado por", index: "source", type: "text", custom: ({ row }) => {
								return <DataSource source={row.source} />;
							}
						},
						{
							collumnLabel: "Tamanho", index: "size", type: "text", custom: ({ row }) => {
								return formatBytes(row.size);
							}
						},
						{ collumnLabel: "Download", index: "download", type: "download" },
					]}
					data={backups}
				/>
			</div>
			<div className="card-visibility">
				{backups &&
					backups?.map((backup, index) => (
						<Card key={index} data={backup}>
							<p className="card-content-title">{backup.bucketKey}</p>
						</Card>
					))}
			</div>
			<Pagination
				rowsPerPage={8}
				maxPage={maxPage}
				changePage={(page) =>
					setCurrentPage(page)
				}
			/>
		</React.Fragment>
	);
};

export default ListBackup;
